import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { LeadResponse } from '@orion/contracts';
import { useLeadsQuery } from '../leads/api.js';
import { useSidebarCountsQuery } from '../me/api.js';
import { MobileBottomNav, avatarColor, initialsOf, statusPillStyle } from './MobileNav.js';
import { QuickLogSheet } from './QuickLogSheet.js';

const isDesignQuickAddPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'MobileQuickAdd';

type Chip = 'mine' | 'hot' | 'followups' | 'all' | 'unassigned';
const CHIPS: Array<{ value: Chip; label: string; hot?: boolean }> = [
  { value: 'mine', label: 'My leads' },
  { value: 'hot', label: 'Hot', hot: true },
  { value: 'followups', label: 'Follow-ups today' },
  { value: 'all', label: 'All' },
  { value: 'unassigned', label: 'Unassigned' },
];

function formatFollowUp(iso: string | null): { text: string; overdue: boolean } | null {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  const overdue = d.getTime() < now.getTime();
  if (dayDiff === 0) return { text: `Today ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`, overdue };
  if (dayDiff === 1) return { text: 'Tomorrow', overdue: false };
  if (dayDiff < 0) return { text: `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, overdue: true };
  return { text: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), overdue: false };
}

function LeadRow({ lead }: { lead: LeadResponse }) {
  const followUp = formatFollowUp(lead.nextFollowUpAt);
  const pill = statusPillStyle(lead.status.color);
  return (
    <li>
      <Link to="/m/leads/$leadId" params={{ leadId: lead.id }} className="flex flex-col gap-1.5 rounded-control border border-border bg-surface px-3.5 py-3 no-underline text-ink shadow-card">
        <span className="flex h-6 items-center gap-1.5">
          <span className="text-[15px] font-semibold">{lead.fullName}</span>
          {lead.temperature === 'hot' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7781A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Hot">
              <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3s1 2 2 2c0-3-1-5 1-8" />
            </svg>
          )}
          <span className="flex-1" />
          <span className="flex h-6 items-center gap-1.5 rounded-pill px-2.5 text-xs font-semibold" style={{ background: pill.bg, color: pill.fg }}>
            <span className="size-1.5 rounded-full" style={{ background: pill.fg }} />
            {lead.status.name}
          </span>
        </span>
        <span className="flex h-5 items-center gap-2 text-[13px] text-text-muted">
          <span className="min-w-0 flex-1 truncate">{lead.companyName ?? '—'}</span>
          {followUp ? (
            <span className={`flex items-center gap-1 font-semibold ${followUp.overdue ? 'text-danger' : 'text-text-secondary'}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              {followUp.text}
            </span>
          ) : (
            <span className="font-semibold text-warning-text">No follow-up set</span>
          )}
          {lead.owner ? (
            <span aria-label={`Owner ${lead.owner.displayName}`} className="flex size-5 shrink-0 items-center justify-center rounded-full text-[9px] font-[650] text-white" style={{ background: avatarColor(lead.owner.displayName) }}>
              {initialsOf(lead.owner.displayName)}
            </span>
          ) : (
            <span aria-label="Unassigned" className="flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-border-strong text-text-secondary">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
              </svg>
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}

export function MobileLeadsPage() {
  const [chip, setChip] = useState<Chip>('all');
  const { data: sidebarCounts } = useSidebarCountsQuery();

  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const filters =
    chip === 'mine'
      ? {}
      : chip === 'hot'
        ? { temperature: 'hot' as const }
        : chip === 'followups'
          ? { nextFollowUpBefore: endOfToday.toISOString() }
          : chip === 'unassigned'
            ? { unassigned: true }
            : {};
  const { data, isLoading } = useLeadsQuery({ ...filters, sort: 'nextFollowUpAt', pageSize: 50 });
  const leads = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const [showQuickAdd, setShowQuickAdd] = useState(isDesignQuickAddPreview);

  return (
    <div className="relative flex h-dvh flex-col bg-ground text-ink">
      <header className="flex h-14 shrink-0 items-center gap-1 pl-4 pr-1.5">
        <h1 className="m-0 flex flex-1 items-baseline gap-2 text-[20px] font-[650] tracking-[-0.01em]">
          Leads <span className="text-[13px] font-medium text-text-muted tabular-nums">{total}</span>
        </h1>
        <button type="button" aria-label="Search leads" className="flex size-11 items-center justify-center rounded-control">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
        </button>
        <button type="button" aria-label="Filter leads" className="flex size-11 items-center justify-center rounded-control">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 5h18l-7 8v6l-4 2v-8z" />
          </svg>
        </button>
        <Link to="/leads/new" aria-label="New lead" className="flex size-11 items-center justify-center">
          <span className="flex size-8 items-center justify-center rounded-input bg-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
        </Link>
      </header>

      <div role="tablist" aria-label="Saved views" className="flex shrink-0 gap-2 overflow-x-auto whitespace-nowrap py-1 pl-4">
        {CHIPS.map((c) => {
          const selected = chip === c.value;
          return (
            <button
              key={c.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setChip(c.value)}
              className={`flex h-10 shrink-0 items-center gap-1.5 rounded-pill border px-3.5 text-sm ${selected ? 'border-night bg-night font-semibold text-white' : 'border-border bg-surface font-medium text-text-secondary'}`}
            >
              {c.hot && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7781A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3s1 2 2 2c0-3-1-5 1-8" />
                </svg>
              )}
              {c.label}
            </button>
          );
        })}
      </div>

      <div className="flex h-9 shrink-0 items-center justify-between px-4 text-[13px] text-text-muted">
        <button type="button" className="flex h-9 items-center gap-1 text-text-secondary">
          Sorted by <span className="font-semibold text-ink">next follow-up</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        <span className="tabular-nums">{total} leads</span>
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-1">
        {!isLoading && (
          <ul className="m-0 flex flex-col gap-2" style={{ listStyle: 'none' }}>
            {leads.map((lead) => (
              <LeadRow key={lead.id} lead={lead} />
            ))}
          </ul>
        )}
      </main>

      <MobileBottomNav active="leads" tasksBadge={sidebarCounts?.tasks} />

      {showQuickAdd && (
        <QuickLogSheet
          leadId={isDesignQuickAddPreview ? leads[0]?.id : undefined}
          previewNotes={isDesignQuickAddPreview ? 'Confirmed Q4 rollout timeline. Jordan wants the revised proposal by Friday — loop in Sofia on pricing.' : undefined}
          onClose={() => setShowQuickAdd(false)}
        />
      )}
    </div>
  );
}
